import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Col, Container, Modal, ModalBody, ModalHeader, Row } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment, { Moment } from "moment";
import {
  HISTORY_POST_LOADING,
  OPENAI_GET_TEMPLATES_LOADING,
} from "../../constants/loading.constant";
import { useGlobalCore } from "../../cores/globals";
import { useSocialPostCore } from "../../cores/socialPost";
import { Calendar } from "../component/socialCalendar/Calendar";
import { SocialCalendarBar } from "../component/socialCalendar/SocialCalendarBar";
import { UpdateLoadingCard } from "../component/UpdateLoadingCard";
import Content from "../layout/Content";
import { Header2 } from "../layout/Header2";
import Layout from "../layout/Layout";
import SideModal from "../layout/SideModal";
import { useIntl } from "react-intl";
import { useMeCore } from "../../cores/me";
import { useNotificationCore } from "../../cores/notifications";
import { useRouterCore } from "../../cores/router";
import { MONTH } from "../utils/dateUtils";
import {
  ADD_MENTION_MODAL,
  ADD_SOCIAL_IMAGE_MODAL,
  ADD_SOCIAL_VIDEO_MODAL,
  EMOJI_FORM,
  HASHTAGS_SOCIAL_FORM,
  OPENAI_SOCIAL_FORM,
  SCHEDULE_POST_MODAL,
  TEMPLATES_SOCIAL_FORM,
} from "../../constants/modal.constant";
import { getUnifiedSocialCalendarPostRange } from "../component/socialCalendar/socialCalendarFetchRange";
import { getSocialPostCalendarMoment } from "../component/socialCalendar/socialPostCalendarMoment";
import { SocialCalendarPostComposer } from "../component/socialCalendar/SocialCalendarPostComposer";
import { PostForm } from "../component/socialPost/PostForm";
import ImageForm from "../component/socialPost/modals/ImageForm";
import VideoForm from "../component/socialPost/modals/VideoForm";
import { MentionForm } from "../component/socialPost/modals/MentionForm";
import HashtagsForm from "../component/socialPost/modals/HashtagsForm";
import TemplatesForm from "../component/socialPost/modals/TemplatesForm";
import OpenAiForm from "../component/socialPost/modals/OpenAiForm";
import { SocialConnectAccountSideModals } from "../component/social/SocialConnectAccountSideModals";
import { SocialType } from "../../cores/socialPost/interfaces/postError";
import { SocialPost } from "../../cores/socialPost/interfaces/api/get-social-posts";
import { scheduleDateStringForApiFromDateAndHour } from "../../cores/socialPost/helpers/scheduleDateForApi";
import { useGoogleDriveImagePicker } from "../../hooks/useGoogleDriveImagePicker";

function ensureFutureDropMoment(candidate: Moment): Moment {
  const now = moment();
  if (candidate.isAfter(now)) {
    return candidate.clone().second(0).millisecond(0);
  }
  if (candidate.isSame(now, "day")) {
    return now.clone().add(1, "hour").second(0).millisecond(0);
  }
  return now.clone().add(1, "hour").second(0).millisecond(0);
}

export default function SocialCalendar() {
  const intl = useIntl();
  const {
    socialPosts,
    date,
    setDate,
    resetComposerDraft,
    selectPost,
    setDuplicateScheduleSourcePostId,
    removeAllImages,
    removeVideo,
    getSocialPosts,
    addImages,
  } = useSocialPostCore();
  const {
    isModal,
    turnOffModal,
    getModalContent,
    getModalVisibility,
    isLoading,
  } = useGlobalCore();
  const { companyRole, isAdmin, isReseller, getMeSocialSettings, company } =
    useMeCore();
  const { goToSocial } = useRouterCore();
  const { getNotifications, getUnreadNotifications } =
    useNotificationCore();
  const [calendarView, setCalendarView] = useState<"day" | "week" | "month">(
    "month"
  );
  const [bestHoursNetwork, setBestHoursNetwork] = useState<
    "none" | "facebook" | "instagram" | "linkedin" | "youtube"
  >("none");
  const [showBestHoursPercent, setShowBestHoursPercent] = useState(false);
  const [now, setNow] = useState(() => new Date());
  const [composerOpen, setComposerOpen] = useState(false);
  const [composerDate, setComposerDate] = useState<Moment | undefined>(undefined);
  const [calendarFilterProvider, setCalendarFilterProvider] = useState("all");
  const [calendarFilterTarget, setCalendarFilterTarget] = useState("post");
  const { openGoogleDriveImages, googleDriveModals } = useGoogleDriveImagePicker({
    companyId: company?.id,
    onFilesSelected: addImages,
  });

  /**
   * Una sola getSocialPosts: addMany sustituye todo el árbol; dos rangos seguidos se pisan.
   * Unimos ventana visible del calendario + ventana del historial y repetimos tras ~2 s por lag API.
   */
  const refreshAfterComposerSchedule = useCallback(() => {
    if (!company?.id) return;
    const { since, until } = getUnifiedSocialCalendarPostRange({
      calendarView,
      date,
    });

    const run = () => {
      getSocialPosts(
        company.id,
        since,
        until,
        calendarFilterProvider,
        "",
        calendarFilterTarget
      );
      void getNotifications(company.id, 1, { type: "short" });
      void getUnreadNotifications(company.id);
    };
    run();
    window.setTimeout(run, 2200);
  }, [
    company?.id,
    calendarView,
    date,
    calendarFilterProvider,
    calendarFilterTarget,
    getSocialPosts,
    getNotifications,
    getUnreadNotifications,
  ]);

  const browserTimeZone = useMemo(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    } catch {
      return "UTC";
    }
  }, []);

  /* Mismo criterio que SocialPost: el selector de redes y el envío usan datos de getMeSocialSettings. */
  useEffect(() => {
    if (company?.id) getMeSocialSettings(company.id);
  }, [company?.id, getMeSocialSettings]);

  useEffect(() => {
    if (
      !companyRole?.social &&
      !companyRole?.owner &&
      !companyRole?.admin &&
      !companyRole?.agent &&
      !isAdmin &&
      !isReseller
    ) goToSocial();
  }, [goToSocial, companyRole, isAdmin, isReseller]);

  useEffect(() => {}, [socialPosts]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setNow(new Date());
    }, 30000);
    return () => window.clearInterval(timer);
  }, []);

  const currentTime = useMemo(
    () =>
      now.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }),
    [now]
  );

  const nextPeriod = useCallback(() => {
    if (calendarView === "day") {
      setDate(moment(date).add(1, "d"));
      return;
    }
    if (calendarView === "week") {
      setDate(moment(date).add(1, "w"));
      return;
    }
    setDate(moment(date).add(1, "M"));
  }, [calendarView, date, setDate]);

  const previousPeriod = useCallback(() => {
    if (calendarView === "day") {
      setDate(moment(date).subtract(1, "d"));
      return;
    }
    if (calendarView === "week") {
      setDate(moment(date).subtract(1, "w"));
      return;
    }
    setDate(moment(date).subtract(1, "M"));
  }, [calendarView, date, setDate]);

  const openCreatePostModal = useCallback(
    (day: Moment, hour?: number) => {
      let selected =
        hour !== undefined
          ? moment(day).hour(hour).minute(0).second(0).millisecond(0)
          : moment(day).hour(0).minute(0).second(0).millisecond(0);
      if (!selected.isAfter(moment())) {
        selected = moment().add(1, "hour").second(0).millisecond(0);
      }
      /* No usar reset(): vacía socialPosts en el store y las publicaciones del calendario “desaparecen”. */
      resetComposerDraft();
      setComposerDate(selected);
      setComposerOpen(true);
    },
    [resetComposerDraft]
  );

  const openEditPostModal = useCallback(
    (post: SocialPost) => {
      setDuplicateScheduleSourcePostId(null);
      removeVideo();
      removeAllImages();
      const m = getSocialPostCalendarMoment(post);
      if (!post.post.postAt && m.isValid()) {
        selectPost({
          ...post,
          post: {
            ...post.post,
            scheduleDate: scheduleDateStringForApiFromDateAndHour(
              m.toDate(),
              m.format("HH:mm")
            ),
          },
        });
      } else {
        selectPost(post);
      }
      setComposerDate(m.isValid() ? m : moment());
      setComposerOpen(true);
    },
    [
      removeAllImages,
      removeVideo,
      selectPost,
      setDuplicateScheduleSourcePostId,
    ]
  );

  /**
   * Arrastrar a otro día/hora: abre el modal del posteador con esa fecha.
   * Publicados → modo republicar (duplicar) en fecha futura.
   * Programados → editar/reagendar en el modal.
   */
  const onReschedulePost = useCallback(
    (post: SocialPost, nextMoment: Moment) => {
      if (!nextMoment.isValid()) return;
      const target = ensureFutureDropMoment(nextMoment);
      const nextStr = scheduleDateStringForApiFromDateAndHour(
        target.toDate(),
        target.format("HH:mm")
      );
      const isPublished = Boolean(post.post.postAt);

      removeVideo();
      removeAllImages();
      selectPost({
        ...post,
        post: {
          ...post.post,
          scheduleDate: nextStr,
        },
      });
      setDuplicateScheduleSourcePostId(isPublished ? post.post.id : null);
      setComposerDate(target);
      setComposerOpen(true);
    },
    [
      removeAllImages,
      removeVideo,
      selectPost,
      setDuplicateScheduleSourcePostId,
    ]
  );
 
  return (
    <Layout>
      <Header2 
        title={intl.formatMessage({ id: "app.sidebar.socialMediaCalendar" })}
      />
      <Content>
        <Container fluid className="social-calendar-page">
          <Row>
            <Col xs="12">
              <SocialCalendarBar
                calendarView={calendarView}
                onChangeCalendarView={setCalendarView}
                bestHoursNetwork={bestHoursNetwork}
                onChangeBestHoursNetwork={setBestHoursNetwork}
                showBestHoursPercent={showBestHoursPercent}
                onToggleBestHoursPercent={setShowBestHoursPercent}
                filterProvider={calendarFilterProvider}
                filterTarget={calendarFilterTarget}
                onFilterProviderChange={setCalendarFilterProvider}
                onFilterTargetChange={setCalendarFilterTarget}
              />
              <UpdateLoadingCard
                loadingId={HISTORY_POST_LOADING}
                text="Actualizando Publicaciones"
              />
              <div className="social-calendar-top-meta">
                <div className="social-calendar-top-meta__right">
                  <div className="social-calendar-bar__period-nav social-calendar-top-meta__period mr-2">
                    <button
                      type="button"
                      className="social-calendar-bar__nav-btn"
                      onClick={previousPeriod}
                      aria-label="Anterior"
                    >
                      <FontAwesomeIcon
                        icon={["fas", "chevron-left"]}
                        size="1x"
                        color="#64748B"
                      />
                    </button>
                    <span className="social-calendar-bar__month-label">
                      {calendarView === "day"
                        ? moment(date).format("DD MMM YYYY")
                        : calendarView === "week"
                        ? `${moment(date).startOf("week").format("DD MMM")} - ${moment(date)
                            .endOf("week")
                            .format("DD MMM YYYY")}`
                        : MONTH[moment(date).format("M")] + " " + moment(date).format("YYYY")}
                    </span>
                    <button
                      type="button"
                      className="social-calendar-bar__nav-btn"
                      onClick={nextPeriod}
                      aria-label="Siguiente"
                    >
                      <FontAwesomeIcon
                        icon={["fas", "chevron-right"]}
                        size="1x"
                        color="#64748B"
                      />
                    </button>
                  </div>
                  <button
                    type="button"
                    className="social-calendar-top-meta__today-btn mr-2"
                    onClick={() => setDate(moment())}
                  >
                    {intl.formatMessage({ id: "app.social.goToday" })}
                  </button>
                  <div className="social-calendar-timezone-pill">
                    <FontAwesomeIcon icon={["far", "clock"]} className="mr-2" />
                    <span>{`${currentTime} - ${browserTimeZone}`}</span>
                    <FontAwesomeIcon
                      icon={["fas", "chevron-down"]}
                      className="ml-2"
                      size="sm"
                    />
                  </div>
                </div>
              </div>
              <Container
                fluid
                className="d-flex flex-wrap justify-content-center align-items-baseline p-0"
              >
                <Calendar
                  view={calendarView}
                  bestHoursNetwork={bestHoursNetwork}
                  showBestHoursPercent={showBestHoursPercent}
                  onCreatePostAt={openCreatePostModal}
                  onOpenPostComposer={openEditPostModal}
                  onReschedulePost={onReschedulePost}
                />
              </Container>
            </Col>
          </Row>
        </Container>
      </Content>
      <Modal
        isOpen={composerOpen}
        toggle={() => {
          setComposerOpen(false);
          setDuplicateScheduleSourcePostId(null);
        }}
        centered
        size="xl"
        className="social-calendar-composer-modal"
      >
        <ModalHeader
          toggle={() => {
            setComposerOpen(false);
            setDuplicateScheduleSourcePostId(null);
          }}
        >
          {intl.formatMessage({ id: "app.socialPostSelector.createTitle" })}
        </ModalHeader>
        <ModalBody>
          <SocialCalendarPostComposer
            selectedDate={composerDate}
            onChangeSelectedDate={setComposerDate}
            onScheduledSuccess={() => setComposerOpen(false)}
            onSavedSuccess={() => setComposerOpen(false)}
            onDeletedSuccess={() => setComposerOpen(false)}
            onPostsRefresh={refreshAfterComposerSchedule}
          />
        </ModalBody>
      </Modal>
      <SideModal
        active={isModal(SCHEDULE_POST_MODAL)}
        visible={getModalVisibility(SCHEDULE_POST_MODAL)}
        onClose={() => turnOffModal(SCHEDULE_POST_MODAL)}
      >
        {getModalContent(SCHEDULE_POST_MODAL) || (
          <PostForm
            onClose={() => turnOffModal(SCHEDULE_POST_MODAL)}
            onReset={resetComposerDraft}
            onAfterScheduleSuccess={refreshAfterComposerSchedule}
            repost={false}
            socialType={SocialType.Post}
          />
        )}
      </SideModal>
      <SideModal
        active={isModal(ADD_SOCIAL_IMAGE_MODAL)}
        visible={getModalVisibility(ADD_SOCIAL_IMAGE_MODAL)}
        onClose={() => turnOffModal(ADD_SOCIAL_IMAGE_MODAL)}
      >
        <ImageForm onOpenGoogleDrive={openGoogleDriveImages} />
      </SideModal>
      <SideModal
        active={isModal(ADD_SOCIAL_VIDEO_MODAL)}
        visible={getModalVisibility(ADD_SOCIAL_VIDEO_MODAL)}
        onClose={() => turnOffModal(ADD_SOCIAL_VIDEO_MODAL)}
      >
        <VideoForm />
      </SideModal>
      <SideModal
        active={isModal(EMOJI_FORM)}
        visible={getModalVisibility(EMOJI_FORM)}
        onClose={() => turnOffModal(EMOJI_FORM)}
      >
        {getModalContent(EMOJI_FORM)}
      </SideModal>
      <SideModal
        active={isModal(TEMPLATES_SOCIAL_FORM)}
        visible={getModalVisibility(TEMPLATES_SOCIAL_FORM)}
        onClose={() => turnOffModal(TEMPLATES_SOCIAL_FORM)}
      >
        <TemplatesForm />
      </SideModal>
      <SideModal
        active={isModal(OPENAI_SOCIAL_FORM)}
        visible={getModalVisibility(OPENAI_SOCIAL_FORM)}
        onClose={() => turnOffModal(OPENAI_SOCIAL_FORM)}
        showSppiner={isLoading(OPENAI_GET_TEMPLATES_LOADING)}
      >
        <OpenAiForm />
      </SideModal>
      <SideModal
        active={isModal(ADD_MENTION_MODAL)}
        visible={getModalVisibility(ADD_MENTION_MODAL)}
        onClose={() => turnOffModal(ADD_MENTION_MODAL)}
      >
        <MentionForm />
      </SideModal>
      <SideModal
        active={isModal(HASHTAGS_SOCIAL_FORM)}
        visible={getModalVisibility(HASHTAGS_SOCIAL_FORM)}
        onClose={() => turnOffModal(HASHTAGS_SOCIAL_FORM)}
      >
        <HashtagsForm />
      </SideModal>
      <SocialConnectAccountSideModals />
      {googleDriveModals}
    </Layout>
  );
}
